import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Newspaper, ExternalLink, RefreshCw, AlertCircle } from "lucide-react";
import { API } from "../constants";

const REFRESH_MS = 5 * 60 * 1000; // 5 minutos

function timeAgo(isoDate) {
  if (!isoDate) return "";
  const seconds = Math.floor((Date.now() - new Date(isoDate).getTime()) / 1000);
  if (seconds < 60) return "ahora";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `hace ${hours} h`;
  const days = Math.floor(hours / 24);
  return `hace ${days} d`;
}

export function NewsFeed() {
  const [news, setNews] = useState([]);
  const [source, setSource] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);

  const fetchNews = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/news`, { params: { limit: 30 }, timeout: 15000 });
      setNews(res.data?.news || []);
      setSource(res.data?.source || "");
      setUpdatedAt(res.data?.updated_at || null);
      setError(null);
    } catch (e) {
      console.error("Error cargando noticias:", e);
      setError("No se pudieron cargar las noticias. Reintentando automáticamente…");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNews();
    const interval = setInterval(fetchNews, REFRESH_MS);
    return () => clearInterval(interval);
  }, [fetchNews]);

  return (
    <div className="bg-[#0d0d0f] rounded-xl border border-gray-800 p-5 h-full flex flex-col" data-testid="news-feed">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <Newspaper className="w-5 h-5 text-cyan-400" /> Noticias
        </h3>
        <div className="flex items-center gap-2 text-xs text-gray-500">
          {updatedAt && <span>Act. {timeAgo(updatedAt)}</span>}
          <button onClick={fetchNews} className="p-1.5 hover:bg-gray-800 rounded-lg transition-colors" title="Actualizar">
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {loading && (
        <div className="space-y-3">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="animate-pulse flex gap-3">
              <div className="w-16 h-16 bg-gray-800 rounded-lg flex-shrink-0" />
              <div className="flex-1 space-y-2 py-1">
                <div className="h-3 bg-gray-800 rounded w-3/4" />
                <div className="h-3 bg-gray-800 rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && error && (
        <div className="flex items-center gap-2 text-amber-400 text-sm bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
          <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
        </div>
      )}

      {!loading && !error && news.length === 0 && (
        <p className="text-gray-500 text-sm">No hay noticias disponibles ahora mismo.</p>
      )}

      <div className="space-y-2 overflow-y-auto pr-1 flex-1 min-h-0">
        {news.map((item, idx) => (
          <a
            key={item.id || idx}
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex gap-3 p-2.5 rounded-lg hover:bg-gray-900/70 transition-colors group"
          >
            {item.image ? (
              <img
                src={item.image}
                alt=""
                loading="lazy"
                className="w-16 h-16 object-cover rounded-lg flex-shrink-0 bg-gray-800"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            ) : (
              <div className="w-16 h-16 rounded-lg flex-shrink-0 bg-gray-800/60 flex items-center justify-center">
                <Newspaper className="w-6 h-6 text-gray-600" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className="text-sm text-gray-100 font-medium leading-snug line-clamp-2 group-hover:text-cyan-300 transition-colors">
                {item.title}
              </p>
              <div className="flex items-center gap-2 mt-1.5 text-xs text-gray-500">
                <span className="text-cyan-500/80">{item.source}</span>
                <span>·</span>
                <span>{timeAgo(item.published_at)}</span>
                <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              {item.categories?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {item.categories.slice(0, 3).map((cat) => (
                    <span key={cat} className="text-[10px] px-1.5 py-0.5 rounded bg-gray-800 text-gray-400">{cat}</span>
                  ))}
                </div>
              )}
            </div>
          </a>
        ))}
      </div>

      {source && <p className="text-[10px] text-gray-600 mt-3 pt-3 border-t border-gray-800/60">Fuente: {source}</p>}
    </div>
  );
}

export default NewsFeed;
